import { useState, useCallback, useMemo, useEffect } from 'react';
import { RowConfig } from '@/shared/types/table';
import { toggleRowSelection, isAllSelected, isSomeSelected } from '../utils/selection';

export interface UseTableSelectionOptions<T> {
  /** Si devuelve false, la fila no se puede seleccionar (checkbox deshabilitado en UI). */
  isRowSelectable?: (row: RowConfig<T>) => boolean;
}

export const useTableSelection = <T>(
  rows: RowConfig<T>[],
  multiple = true,
  options?: UseTableSelectionOptions<T>,
) => {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const selectableRows = useMemo(
    () => (options?.isRowSelectable ? rows.filter((row) => options.isRowSelectable!(row)) : rows),
    [rows, options?.isRowSelectable],
  );

  const allSelected = useMemo(
    () => isAllSelected(selectableRows, selectedIds),
    [selectableRows, selectedIds],
  );

  const someSelected = useMemo(
    () => isSomeSelected(selectableRows, selectedIds),
    [selectableRows, selectedIds],
  );

  // Quitar de la selección ids que dejaron de ser seleccionables (p. ej. excludedRows)
  useEffect(() => {
    if (!options?.isRowSelectable) return;
    setSelectedIds((prev) =>
      prev.filter((id) => {
        const row = rows.find((r) => r.id === id);
        return row && options.isRowSelectable!(row);
      }),
    );
  }, [rows, options?.isRowSelectable]);

  const handleRowSelect = useCallback(
    (row: RowConfig<T>) => {
      if (options?.isRowSelectable && !options.isRowSelectable(row)) return;
      if (!multiple) {
        setSelectedIds([row.id]);
        return;
      }
      setSelectedIds((prev) => toggleRowSelection(prev, row.id));
    },
    [multiple, options?.isRowSelectable],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(selectableRows.map((row) => row.id));
      } else {
        setSelectedIds([]);
      }
    },
    [selectableRows],
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const selectRows = useCallback((ids: (string | number)[]) => {
    setSelectedIds(ids);
  }, []);

  const isSelected = useCallback(
    (rowId: string | number) => selectedIds.includes(rowId),
    [selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    allSelected,
    someSelected,
    handleRowSelect,
    handleSelectAll,
    clearSelection,
    selectRows,
    isSelected,
  };
};
